import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Input, Badge } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { LEAD_TYPE_FORM_OPTIONS, LEAD_SOURCE_FORM_OPTIONS } from '../../lib/leadMeta';

const { FiPlus, FiArrowLeft, FiTrash2, FiEdit2, FiBarChart2, FiRefreshCw, FiSend, FiCopy } = FiIcons;

const LEAD_TYPE_OPTIONS = LEAD_TYPE_FORM_OPTIONS.filter((o) => o.value !== 'recruit');

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'dnc', label: 'DNC' },
];

const LEAD_SOURCE_OPTIONS = LEAD_SOURCE_FORM_OPTIONS;

const MERGE_FIELDS = ['{{first_name}}', '{{last_name}}', '{{agency_name}}'];

const emptyFilters = () => ({
  lead_type: [],
  status: [],
  lead_source: [],
});

const emptyForm = () => ({
  name: '',
  subject: '',
  body_html: '',
  audience_filters: emptyFilters(),
});

function CampaignStatusBadge({ status }) {
  const map = {
    draft: { variant: 'default', label: 'Draft' },
    sending: { variant: 'warning', label: 'Sending' },
    sent: { variant: 'success', label: 'Sent' },
  };
  const x = map[status] || { variant: 'default', label: status || '—' };
  return <Badge variant={x.variant}>{x.label}</Badge>;
}

function pct(numerator, denominator) {
  if (!denominator || denominator <= 0) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function MultiSelectGroup({ label, options, selected, onChange }) {
  const toggle = (value) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                active
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MergeFieldChips({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {MERGE_FIELDS.map((token) => (
        <button
          key={token}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsert(token)}
          className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
        >
          {token}
        </button>
      ))}
    </div>
  );
}

function CampaignList() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.agency_id) {
      setCampaigns([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.campaigns.getAll(user.agency_id);
      setCampaigns(rows);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load campaigns',
        message: err?.message || 'Try again.',
      });
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete draft "${c.name}"?`)) return;
    setDeletingId(c.id);
    try {
      await api.campaigns.delete(c.id);
      setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
      addToast({ type: 'success', title: 'Campaign deleted' });
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err?.message });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (c) => {
    setDuplicatingId(c.id);
    try {
      await api.campaigns.duplicate(c.id);
      await load();
      addToast({ type: 'success', title: 'Campaign duplicated successfully' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Duplicate failed',
        message: err?.message || 'Try again.',
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">Send one-off emails to filtered lead audiences via Brevo.</p>
        </div>
        <Button size="sm" onClick={() => navigate('/campaigns/new')}>
          <SafeIcon icon={FiPlus} className="mr-2 w-4 h-4" />
          New Campaign
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Spinner size="lg" className="text-primary" />
            <p className="text-sm text-gray-500">Loading campaigns…</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <SafeIcon icon={FiSend} className="w-10 h-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-700">No campaigns yet</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              Create a draft, preview your audience, and send when you are ready.
            </p>
            <Button size="sm" className="mt-4" onClick={() => navigate('/campaigns/new')}>
              <SafeIcon icon={FiPlus} className="mr-2 w-4 h-4" />
              New Campaign
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns.map((c) => (
                <tr key={c.id} className="bg-white hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{c.name}</td>
                  <td className="px-6 py-4">
                    <CampaignStatusBadge status={c.status} />
                  </td>
                  <td className="px-6 py-4 text-gray-700">{c.total_recipients ?? 0}</td>
                  <td className="px-6 py-4 text-gray-700">{pct(c.open_count, c.sent_count)}</td>
                  <td className="px-6 py-4 text-gray-700">{pct(c.click_count, c.sent_count)}</td>
                  <td className="px-6 py-4 text-gray-600 text-xs whitespace-nowrap">
                    {c.sent_at
                      ? format(parseISO(c.sent_at), 'MMM d, yyyy h:mm a')
                      : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {c.status === 'draft' ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/campaigns/${c.id}/edit`)}
                            disabled={duplicatingId === c.id}
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void handleDelete(c)}
                            disabled={deletingId === c.id || duplicatingId === c.id}
                            isLoading={deletingId === c.id}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-1 text-red-500" />
                            Delete
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/campaigns/${c.id}`)}
                          disabled={duplicatingId === c.id}
                        >
                          <SafeIcon icon={FiBarChart2} className="w-4 h-4 mr-1" />
                          View stats
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleDuplicate(c)}
                        disabled={duplicatingId === c.id || deletingId === c.id}
                        isLoading={duplicatingId === c.id}
                      >
                        <SafeIcon icon={FiCopy} className="w-4 h-4 mr-1" />
                        Duplicate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function CampaignBuilder({ isEdit }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [audienceCount, setAudienceCount] = useState(0);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    setLoading(true);
    api.campaigns
      .getById(id)
      .then((row) => {
        if (cancelled) return;
        if (!row || row.status !== 'draft') {
          addToast({ type: 'warning', title: 'Campaign is not editable' });
          navigate(row ? `/campaigns/${row.id}` : '/campaigns');
          return;
        }
        setForm({
          name: row.name || '',
          subject: row.subject || '',
          body_html: row.body_html || '',
          audience_filters: {
            lead_type: row.audience_filters?.lead_type ?? [],
            status: row.audience_filters?.status ?? [],
            lead_source: row.audience_filters?.lead_source ?? [],
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Could not load campaign', message: err?.message });
          navigate('/campaigns');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, id, navigate, addToast]);

  const insertMergeField = (token) => {
    const el = bodyRef.current;
    setForm((prev) => {
      const prevBody = prev.body_html;
      const start = el?.selectionStart ?? prevBody.length;
      const end = el?.selectionEnd ?? start;
      const next = prevBody.slice(0, start) + token + prevBody.slice(end);
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return { ...prev, body_html: next };
    });
  };

  const handlePreview = async () => {
    if (!user?.agency_id) return;
    setPreviewLoading(true);
    try {
      const result = await api.campaigns.previewAudience(user.agency_id, form.audience_filters);
      setPreview(result);
      setAudienceCount(result.count);
    } catch (err) {
      addToast({ type: 'error', title: 'Preview failed', message: err?.message });
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      addToast({ type: 'warning', title: 'Campaign name is required' });
      return false;
    }
    if (!form.subject.trim()) {
      addToast({ type: 'warning', title: 'Subject is required' });
      return false;
    }
    if (!form.body_html.trim()) {
      addToast({ type: 'warning', title: 'Email body is required' });
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validate() || !user?.agency_id) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        body_html: form.body_html,
        audience_filters: form.audience_filters,
        agency_id: user.agency_id,
      };
      if (isEdit && id) {
        await api.campaigns.update(id, payload);
        addToast({ type: 'success', title: 'Draft saved' });
      } else {
        const created = await api.campaigns.create(payload);
        addToast({ type: 'success', title: 'Draft created' });
        navigate(`/campaigns/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      addToast({ type: 'error', title: 'Save failed', message: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const openSendModal = async () => {
    if (!validate()) return;
    setPreviewLoading(true);
    try {
      const result = await api.campaigns.previewAudience(user.agency_id, form.audience_filters);
      setAudienceCount(result.count);
      if (result.count === 0) {
        addToast({ type: 'warning', title: 'No recipients match this audience' });
        return;
      }
      setSendModalOpen(true);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not preview audience', message: err?.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      let campaignId = id;
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        body_html: form.body_html,
        audience_filters: form.audience_filters,
        agency_id: user.agency_id,
      };
      if (isEdit && id) {
        await api.campaigns.update(id, payload);
      } else {
        const created = await api.campaigns.create(payload);
        campaignId = created.id;
      }
      await api.campaigns.send(campaignId);
      addToast({ type: 'success', title: 'Campaign is sending' });
      setSendModalOpen(false);
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      addToast({ type: 'error', title: 'Send failed', message: err?.message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-sm text-gray-500">Loading campaign…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Campaign' : 'New Campaign'}
        </h1>
      </div>

      <Card className="p-6 space-y-5">
        <Input
          label="Campaign name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <Input
          label="Subject"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email body (HTML)</label>
          <textarea
            ref={bodyRef}
            rows={8}
            value={form.body_html}
            onChange={(e) => setForm((f) => ({ ...f, body_html: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <MergeFieldChips onInsert={insertMergeField} />
        </div>

        <div className="border-t border-gray-100 pt-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Audience filters</h2>
          <p className="text-xs text-gray-500">Leave all unselected to include every lead with an email in your agency.</p>
          <MultiSelectGroup
            label="Lead type"
            options={LEAD_TYPE_OPTIONS}
            selected={form.audience_filters.lead_type}
            onChange={(lead_type) =>
              setForm((f) => ({ ...f, audience_filters: { ...f.audience_filters, lead_type } }))
            }
          />
          <MultiSelectGroup
            label="Status"
            options={STATUS_OPTIONS}
            selected={form.audience_filters.status}
            onChange={(status) =>
              setForm((f) => ({ ...f, audience_filters: { ...f.audience_filters, status } }))
            }
          />
          <MultiSelectGroup
            label="Lead source"
            options={LEAD_SOURCE_OPTIONS}
            selected={form.audience_filters.lead_source}
            onChange={(lead_source) =>
              setForm((f) => ({ ...f, audience_filters: { ...f.audience_filters, lead_source } }))
            }
          />
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handlePreview()}
              disabled={previewLoading}
              isLoading={previewLoading}
            >
              Preview Audience
            </Button>
            {preview && (
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{preview.count}</span> recipient
                {preview.count !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          {preview?.sample?.length > 0 && (
            <ul className="text-xs text-gray-500 list-disc pl-5 space-y-0.5">
              {preview.sample.map((s) => (
                <li key={s.id}>
                  {s.name} — {s.email}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={() => void handleSaveDraft()} isLoading={saving}>
            Save as Draft
          </Button>
          <Button type="button" onClick={() => void openSendModal()} disabled={previewLoading}>
            Send Now
          </Button>
        </div>
      </Card>

      <Modal isOpen={sendModalOpen} onClose={() => setSendModalOpen(false)} title="Send campaign">
        <p className="text-sm text-gray-600 mt-2">
          You are about to send to <strong>{audienceCount}</strong> lead
          {audienceCount !== 1 ? 's' : ''}. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={() => setSendModalOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSend()} isLoading={sending}>
            Send now
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [row, logs] = await Promise.all([
        api.campaigns.getById(id),
        api.campaigns.getRecipients(id),
      ]);
      if (!row) {
        addToast({ type: 'error', title: 'Campaign not found' });
        navigate('/campaigns');
        return;
      }
      if (row.status === 'draft') {
        navigate(`/campaigns/${id}/edit`, { replace: true });
        return;
      }
      setCampaign(row);
      setRecipients(logs);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load campaign', message: err?.message });
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const [updated, logs] = await Promise.all([
        api.campaigns.syncStats(id),
        api.campaigns.getRecipients(id),
      ]);
      setCampaign(updated);
      setRecipients(logs);
      setLastSynced(new Date());
      addToast({ type: 'success', title: 'Stats updated' });
    } catch (err) {
      addToast({ type: 'error', title: 'Sync failed', message: err?.message });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  if (!campaign) return null;

  const openRate = pct(campaign.open_count, campaign.sent_count);
  const clickRate = pct(campaign.click_count, campaign.sent_count);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{campaign.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CampaignStatusBadge status={campaign.status} />
          <Button size="sm" variant="outline" onClick={() => void handleSync()} isLoading={syncing}>
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-1" />
            Sync Stats
          </Button>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Sent{' '}
        {campaign.sent_at
          ? format(parseISO(campaign.sent_at), 'MMM d, yyyy h:mm a')
          : '—'}
        {lastSynced && (
          <span className="ml-3 text-gray-400">
            · Last synced {format(lastSynced, 'MMM d, h:mm a')}
          </span>
        )}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Sent', value: campaign.sent_count ?? 0 },
          { label: 'Open Rate', value: openRate },
          { label: 'Click Rate', value: clickRate },
          { label: 'Total Opens', value: campaign.open_count ?? 0 },
          { label: 'Total Clicks', value: campaign.click_count ?? 0 },
          { label: 'Recipients', value: campaign.total_recipients ?? 0 },
        ].map(({ label, value }) => (
          <Card key={label} className="p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-900">Email Preview</h2>
        </div>
        <div className="p-4 bg-white">
          {campaign.body_html ? (
            <>
              <p className="text-xs text-gray-500 mb-2">
                Template preview — merge fields like {'{{first_name}}'} are resolved when the email is sent.
              </p>
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={campaign.body_html}
                className="w-full min-h-[320px] border border-gray-200 rounded-md bg-white"
              />
            </>
          ) : (
            <p className="text-sm text-gray-500 py-8 text-center">No email content.</p>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-900">Recipients</h2>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{recipients.length}</span> recipient
            {recipients.length !== 1 ? 's' : ''}
          </p>
        </div>
        {recipients.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            No recipient logs found for this campaign yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recipients.map((r) => (
                <tr key={r.id} className="bg-white hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-900">{r.lead_name}</td>
                  <td className="px-6 py-3 text-gray-700">{r.email}</td>
                  <td className="px-6 py-3 text-gray-600 text-xs whitespace-nowrap">
                    {r.sent_at ? format(parseISO(r.sent_at), 'MMM d, yyyy h:mm a') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default function Campaigns() {
  return (
    <Routes>
      <Route index element={<CampaignList />} />
      <Route path="new" element={<CampaignBuilder isEdit={false} />} />
      <Route path=":id/edit" element={<CampaignBuilder isEdit />} />
      <Route path=":id" element={<CampaignDetail />} />
    </Routes>
  );
}
